import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { buildGroupPeriodComboSuggests, isPermanentPeriod } from '../../constants/groupPeriods';
import { formatPeriodDateRange } from '../../utils/formatPeriodDates';
import GroupsNestedTeamPeriodChoice from './GroupsNestedTeamPeriodChoice';
import GroupsPeriodBadgePicker from './GroupsPeriodBadgePicker';

const COPY = {
  organization: {
    descriptionLabel: 'About this organization',
    descriptionPlaceholder: 'What this organization is — identity, scope, purpose',
    periodHint: 'A Permanent period is created for this organization automatically.'
  },
  school: {
    descriptionLabel: 'About this school',
    descriptionPlaceholder: 'What this school is — identity, scope, purpose',
    periodHint: 'A Permanent period is created for this school automatically.'
  },
  group: {
    descriptionLabel: 'About the group',
    descriptionPlaceholder: 'What this team is in general'
  }
};

const EMPTY_NEW_PERIOD = { code: '', title: '', start_date: '', end_date: '', is_permanent: false };

export function isNewPeriodDraftValid(draft) {
  if (!draft) return false;
  const code = String(draft.code || '').trim();
  const title = String(draft.title || '').trim();
  if (!code || !title || !draft.start_date) return false;
  const start = new Date(draft.start_date);
  if (Number.isNaN(start.getTime())) return false;
  if (draft.is_permanent) return true;
  if (!draft.end_date) return false;
  const end = new Date(draft.end_date);
  if (Number.isNaN(end.getTime())) return false;
  return start < end;
}

function periodOptionLabel(period) {
  const title = period.title || period.code || 'Period';
  const range = formatPeriodDateRange(period.start_date, period.end_date);
  const dates = range ? ` (${range})` : '';
  if (isPermanentPeriod(period)) return `${title} (Permanent)`;
  return period.code ? `${title} (${period.code})${dates}` : `${title}${dates}`;
}

async function postOrganizationPeriod(organizationGroupId, draft) {
  const isPermanent = Boolean(draft.is_permanent);
  const res = await fetch('/api/periods', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code: String(draft.code || '').trim(),
      title: String(draft.title || '').trim(),
      start_date: draft.start_date,
      end_date: isPermanent ? null : draft.end_date,
      organization_group_id: organizationGroupId,
      is_permanent: isPermanent
    })
  });
  const data = await res.json();
  if (!data.success) {
    throw new Error(data.message || 'Failed to create period');
  }
  return data.data;
}

const GroupsCreateEntityFormFields = forwardRef(function GroupsCreateEntityFormFields(
  {
    entityKind = 'group',
    nameLabel,
    formData,
    setFormData,
    periods,
    organizationGroupId = null,
    organizationName = '',
    onPeriodCreated,
    onPeriodValidityChange,
    disabled = false,
    lockPeriodToDefault = false,
    hideGroupIdentity = false,
    hideGroupPeriodTextFields = false,
    nestedTeamCreate = false,
    periodDescriptionContext = null,
    scopeSubjectName = '',
    suggestedExistingPeriodId = '',
    nestedParentPeriod = null,
    nestedParentTeamName = '',
    allowPermanentPeriodCreate = true,
    allowNewPeriodCreate = true,
    nestedPeriodChoice = 'parent',
    onNestedPeriodChoiceChange
  },
  ref
) {
  const copy = COPY[entityKind] || COPY.group;
  const selectablePeriods = periods || [];
  const selectedPeriod =
    selectablePeriods.find((p) => String(p.id) === String(formData.periodId)) ||
    (nestedParentPeriod &&
    String(nestedParentPeriod.id) === String(formData.periodId)
      ? nestedParentPeriod
      : null);
  const orgLabel = String(organizationName || '').trim();
  const subjectName =
    scopeSubjectName?.trim() ||
    (periodDescriptionContext?.mode === 'team_period'
      ? periodDescriptionContext.teamName
      : formData.name) ||
    'Group';

  const descriptionPlaceholder =
    entityKind === 'group' && orgLabel
      ? `e.g. Students of ${orgLabel}`
      : copy.descriptionPlaceholder;
  const [showCreatePeriod, setShowCreatePeriod] = useState(false);
  const [periodFormError, setPeriodFormError] = useState('');
  const [newPeriod, setNewPeriod] = useState(EMPTY_NEW_PERIOD);
  /** null | 'lasting' | 'varying' — top-level create, or nested after "different period". */
  const [periodVariation, setPeriodVariation] = useState(null);

  const isTopLevelGroupCreate =
    entityKind === 'group' &&
    !lockPeriodToDefault &&
    !nestedTeamCreate &&
    periodDescriptionContext?.mode !== 'team_period';
  const usesNestedOwnVariation = nestedTeamCreate && nestedPeriodChoice === 'own';
  const usesVariationFork = isTopLevelGroupCreate || usesNestedOwnVariation;

  const permanentPeriods = selectablePeriods.filter((p) => isPermanentPeriod(p));
  const termPeriods = selectablePeriods.filter((p) => !isPermanentPeriod(p));
  const periodsForPicker =
    usesVariationFork && periodVariation === 'lasting'
      ? permanentPeriods
      : usesVariationFork && periodVariation === 'varying'
        ? termPeriods
        : selectablePeriods;

  useEffect(() => {
    if (allowPermanentPeriodCreate) return;
    setNewPeriod((p) => (p.is_permanent ? { ...p, is_permanent: false } : p));
  }, [allowPermanentPeriodCreate]);

  useEffect(() => {
    if (allowNewPeriodCreate) return;
    setShowCreatePeriod(false);
    setNewPeriod(EMPTY_NEW_PERIOD);
  }, [allowNewPeriodCreate]);

  const applySuggestedParentPeriod = useCallback(() => {
    const period = nestedParentPeriod;
    if (!period?.id) return;
    const periodId = String(period.id);
    setFormData((prev) => ({
      ...prev,
      periodId,
      ...buildGroupPeriodComboSuggests(
        prev.name || subjectName,
        period,
        organizationName,
        periodDescriptionContext
      )
    }));
  }, [
    nestedParentPeriod,
    organizationName,
    periodDescriptionContext,
    setFormData,
    subjectName
  ]);

  useEffect(() => {
    if (!nestedTeamCreate) return;
    if (nestedPeriodChoice === 'parent' && nestedParentPeriod?.id) {
      applySuggestedParentPeriod();
    }
  }, [
    nestedTeamCreate,
    nestedPeriodChoice,
    nestedParentPeriod?.id,
    applySuggestedParentPeriod
  ]);

  const shouldCreateNewPeriod =
    showCreatePeriod &&
    Boolean(organizationGroupId) &&
    (!nestedTeamCreate || nestedPeriodChoice === 'own') &&
    (!usesVariationFork || periodVariation === 'varying' || periodVariation === 'lasting');

  const groupLabelForPermanent =
    String(formData.name || subjectName || 'Group').trim() || 'Group';
  const ownPermanentTitle = `Permanent -  ${groupLabelForPermanent}`;

  // Default for lasting = create this group's own Permanent (periodId empty).
  // Selecting an existing org/parent Permanent sets periodId and skips create.
  const lastingWillCreatePermanent =
    usesVariationFork &&
    periodVariation === 'lasting' &&
    !formData.periodId &&
    Boolean(organizationGroupId) &&
    allowPermanentPeriodCreate;

  const draftPeriodReady = shouldCreateNewPeriod && isNewPeriodDraftValid(newPeriod);
  const lastingReady =
    usesVariationFork &&
    periodVariation === 'lasting' &&
    (Boolean(formData.periodId) || lastingWillCreatePermanent);
  const varyingReady =
    usesVariationFork &&
    periodVariation === 'varying' &&
    (draftPeriodReady ||
      (Boolean(formData.periodId) &&
        (hideGroupPeriodTextFields || Boolean(String(formData.groupPeriodTitle || '').trim()))));
  const periodReady = usesVariationFork
    ? lastingReady || varyingReady
    : lockPeriodToDefault ||
      draftPeriodReady ||
      (Boolean(formData.periodId) &&
        (hideGroupPeriodTextFields || Boolean(String(formData.groupPeriodTitle || '').trim())));

  const draftPeriodLabel =
    showCreatePeriod && newPeriod.title?.trim()
      ? newPeriod.title.trim()
      : showCreatePeriod && newPeriod.code?.trim()
        ? newPeriod.code.trim()
        : lastingWillCreatePermanent
          ? ownPermanentTitle
          : '';
  const comboPeriodLabel = selectedPeriod
    ? periodOptionLabel(selectedPeriod)
    : draftPeriodLabel || 'period';
  const showGroupPeriodFields =
    entityKind === 'group' &&
    !lockPeriodToDefault &&
    (Boolean(formData.periodId) || draftPeriodReady || lastingWillCreatePermanent) &&
    !(usesVariationFork && periodVariation === 'lasting');
  const gpTitleLabel = `Title of ${subjectName} + ${comboPeriodLabel}`;
  const gpDescLabel = `Description of ${subjectName} + ${comboPeriodLabel} (optional)`;
  const nameFieldLabel =
    entityKind === 'group' ? (nameLabel === 'Group name' ? 'Name of group' : nameLabel) : nameLabel;

  useEffect(() => {
    if (entityKind !== 'group' || lockPeriodToDefault) return;
    onPeriodValidityChange?.({ periodReady });
  }, [entityKind, lockPeriodToDefault, periodReady, onPeriodValidityChange]);

  const applyPeriodToForm = useCallback(
    (period) => {
      if (!period?.id) return;
      setFormData((prev) => {
        const comboName =
          periodDescriptionContext?.mode === 'team_period' ? subjectName : prev.name;
        return {
          ...prev,
          periodId: String(period.id),
          ...buildGroupPeriodComboSuggests(
            comboName,
            period,
            organizationName,
            periodDescriptionContext
          )
        };
      });
      setPeriodFormError('');
    },
    [organizationName, periodDescriptionContext, setFormData, subjectName]
  );

  const applyCreatedPeriod = useCallback(
    (created) => {
      if (onPeriodCreated) onPeriodCreated(created);
      applyPeriodToForm(created);
      setShowCreatePeriod(false);
      setNewPeriod(EMPTY_NEW_PERIOD);
      setPeriodFormError('');
    },
    [applyPeriodToForm, onPeriodCreated]
  );

  // When choosing lasting: default to this group's own Permanent (do not auto-pick org Permanent).
  useEffect(() => {
    if (!usesVariationFork || periodVariation !== 'lasting') return;
    setShowCreatePeriod(false);
    setNewPeriod(EMPTY_NEW_PERIOD);
    setFormData((prev) => {
      const comboName = prev.name || subjectName || 'Group';
      const phantom = {
        title: `Permanent -  ${comboName}`,
        code: 'PERMANENT',
        is_permanent: true
      };
      return {
        ...prev,
        periodId: '',
        ...buildGroupPeriodComboSuggests(
          comboName,
          phantom,
          organizationName,
          periodDescriptionContext
        )
      };
    });
  }, [usesVariationFork, periodVariation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep lasting combo titles in sync when the group name changes (own Permanent default).
  useEffect(() => {
    if (!usesVariationFork || periodVariation !== 'lasting') return;
    if (formData.periodId) {
      const period = permanentPeriods.find((p) => String(p.id) === String(formData.periodId));
      if (!period) return;
      setFormData((prev) => ({
        ...prev,
        ...buildGroupPeriodComboSuggests(
          prev.name || subjectName,
          period,
          organizationName,
          periodDescriptionContext
        )
      }));
      return;
    }
    if (!allowPermanentPeriodCreate) return;
    const phantom = {
      title: ownPermanentTitle,
      code: 'PERMANENT',
      is_permanent: true
    };
    setFormData((prev) => ({
      ...prev,
      ...buildGroupPeriodComboSuggests(
        prev.name || subjectName,
        phantom,
        organizationName,
        periodDescriptionContext
      )
    }));
  }, [formData.name, formData.periodId, usesVariationFork, periodVariation]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      entityKind === 'group' &&
      !lockPeriodToDefault &&
      !nestedTeamCreate &&
      !usesVariationFork &&
      selectablePeriods.length === 0 &&
      organizationGroupId &&
      allowNewPeriodCreate
    ) {
      setShowCreatePeriod(true);
    }
  }, [
    entityKind,
    lockPeriodToDefault,
    nestedTeamCreate,
    usesVariationFork,
    selectablePeriods.length,
    organizationGroupId,
    allowNewPeriodCreate
  ]);

  useEffect(() => {
    if (
      usesVariationFork &&
      periodVariation === 'varying' &&
      termPeriods.length === 0 &&
      organizationGroupId &&
      allowNewPeriodCreate
    ) {
      setShowCreatePeriod(true);
      setNewPeriod((p) => ({ ...p, is_permanent: false }));
    }
  }, [
    usesVariationFork,
    periodVariation,
    termPeriods.length,
    organizationGroupId,
    allowNewPeriodCreate
  ]);

  useEffect(() => {
    if (!nestedTeamCreate) return;
    if (nestedPeriodChoice === 'parent') {
      setShowCreatePeriod(false);
      setNewPeriod(EMPTY_NEW_PERIOD);
      setPeriodFormError('');
      setPeriodVariation(null);
      return;
    }
    // Switching to "different period" — ask lasting vs varying next.
    setPeriodVariation(null);
    setShowCreatePeriod(false);
    setNewPeriod(EMPTY_NEW_PERIOD);
    setPeriodFormError('');
  }, [nestedTeamCreate, nestedPeriodChoice]);

  const resolvePeriodIdForSubmit = useCallback(async () => {
    setPeriodFormError('');
    if (lockPeriodToDefault) {
      return formData.periodId
        ? {
            periodId: String(formData.periodId),
            groupPeriodTitle: formData.groupPeriodTitle,
            groupPeriodDescription: formData.groupPeriodDescription,
            periodWasCreated: false
          }
        : null;
    }

    if (usesVariationFork && periodVariation === 'lasting') {
      if (formData.periodId) {
        return {
          periodId: String(formData.periodId),
          groupPeriodTitle: formData.groupPeriodTitle,
          groupPeriodDescription: formData.groupPeriodDescription,
          periodWasCreated: false
        };
      }
      if (lastingWillCreatePermanent) {
        const draft = {
          code: `PERMANENT_${Date.now().toString(36).toUpperCase()}`,
          title: ownPermanentTitle,
          start_date: new Date().toISOString().slice(0, 10),
          end_date: '',
          is_permanent: true
        };
        try {
          const created = await postOrganizationPeriod(organizationGroupId, draft);
          const comboName = formData.name || subjectName;
          const suggests = buildGroupPeriodComboSuggests(
            comboName,
            created,
            organizationName,
            periodDescriptionContext
          );
          applyCreatedPeriod(created);
          return { periodId: String(created.id), ...suggests, periodWasCreated: true };
        } catch (err) {
          const msg = err.message || 'Failed to create Permanent period';
          setPeriodFormError(msg);
          throw new Error(msg);
        }
      }
      const msg = 'Select a Permanent period for this lasting group.';
      setPeriodFormError(msg);
      throw new Error(msg);
    }

    if (formData.periodId && !shouldCreateNewPeriod) {
      return {
        periodId: String(formData.periodId),
        groupPeriodTitle: formData.groupPeriodTitle,
        groupPeriodDescription: formData.groupPeriodDescription,
        periodWasCreated: false
      };
    }
    if (shouldCreateNewPeriod) {
      if (!isNewPeriodDraftValid(newPeriod)) {
        const msg = newPeriod.is_permanent
          ? 'Enter period code, title, and start date.'
          : 'Enter period code, title, and dates (end after start).';
        setPeriodFormError(msg);
        throw new Error(msg);
      }
      try {
        const created = await postOrganizationPeriod(organizationGroupId, newPeriod);
        const comboName =
          periodDescriptionContext?.mode === 'team_period' ? subjectName : formData.name;
        const suggests = buildGroupPeriodComboSuggests(
          comboName,
          created,
          organizationName,
          periodDescriptionContext
        );
        applyCreatedPeriod(created);
        return {
          periodId: String(created.id),
          ...suggests,
          periodWasCreated: true
        };
      } catch (err) {
        const msg = err.message || 'Failed to create period';
        setPeriodFormError(msg);
        throw new Error(msg);
      }
    }
    if (formData.periodId) {
      return {
        periodId: String(formData.periodId),
        groupPeriodTitle: formData.groupPeriodTitle,
        groupPeriodDescription: formData.groupPeriodDescription,
        periodWasCreated: false
      };
    }
    return null;
  }, [
    applyCreatedPeriod,
    formData.groupPeriodDescription,
    formData.groupPeriodTitle,
    formData.name,
    formData.periodId,
    lastingWillCreatePermanent,
    lockPeriodToDefault,
    newPeriod,
    ownPermanentTitle,
    organizationGroupId,
    organizationName,
    periodDescriptionContext,
    periodVariation,
    shouldCreateNewPeriod,
    subjectName,
    usesVariationFork
  ]);

  useImperativeHandle(
    ref,
    () => ({
      resolvePeriodIdForSubmit,
      isPeriodReady: () => periodReady
    }),
    [resolvePeriodIdForSubmit, periodReady]
  );

  const newPeriodPanel = (
    <div className="groups-create-period-panel">
      <p className="profile-sheet-hint">
        {nestedTeamCreate
          ? 'Creates a new organization school-year period, then enrolls the new team in it when you click Create.'
          : 'New period is created when you click Create below (no separate save step).'}
      </p>
      <div className="groups-create-period-kind-row">
        <span className="profile-sheet-label">Period type</span>
        {allowPermanentPeriodCreate && periodVariation !== 'varying' ? (
          <div className="profile-sheet-toggle" role="tablist" aria-label="Period type">
            <button
              type="button"
              role="tab"
              aria-selected={!newPeriod.is_permanent}
              className={`profile-sheet-toggle-btn${!newPeriod.is_permanent ? ' is-active' : ''}`}
              onClick={() => {
                setNewPeriod((p) => ({ ...p, is_permanent: false }));
                setPeriodFormError('');
              }}
              disabled={disabled}
            >
              Term (i.e. school year)
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={Boolean(newPeriod.is_permanent)}
              className={`profile-sheet-toggle-btn${newPeriod.is_permanent ? ' is-active' : ''}`}
              onClick={() => {
                setNewPeriod((p) => ({ ...p, is_permanent: true, end_date: '' }));
                setPeriodFormError('');
              }}
              disabled={disabled}
            >
              Permanent
            </button>
          </div>
        ) : (
          <p className="profile-sheet-hint" style={{ marginTop: 6 }}>
            Term period — start and end dates required.
          </p>
        )}
        {allowPermanentPeriodCreate && periodVariation !== 'varying' ? (
          <p className="profile-sheet-hint">
            {newPeriod.is_permanent
              ? 'Open-ended — Permanent must be this group’s only period. Prefer the organization Permanent shell when one already exists.'
              : 'Term periods need a start and end date (e.g. a school year, semester, or season).'}
          </p>
        ) : null}
      </div>
      <div className="profile-sheet-form-group">
        <label className="profile-sheet-label" htmlFor="new-period-code">
          Period code *
        </label>
        <input
          id="new-period-code"
          type="text"
          className="profile-sheet-input"
          value={newPeriod.code}
          onChange={(e) => {
            setNewPeriod((p) => ({ ...p, code: e.target.value }));
            setPeriodFormError('');
          }}
          placeholder={newPeriod.is_permanent ? 'e.g. PERMANENT' : 'e.g. 2025-2026'}
          required
          disabled={disabled}
        />
      </div>
      <div className="profile-sheet-form-group">
        <label className="profile-sheet-label" htmlFor="new-period-title">
          Period title *
        </label>
        <input
          id="new-period-title"
          type="text"
          className="profile-sheet-input"
          value={newPeriod.title}
          onChange={(e) => {
            setNewPeriod((p) => ({ ...p, title: e.target.value }));
            setPeriodFormError('');
          }}
          placeholder={
            newPeriod.is_permanent
              ? `e.g. Permanent -  ${groupLabelForPermanent}`
              : 'e.g. 2025–2026'
          }
          required
          disabled={disabled}
        />
      </div>
      <div className="profile-sheet-form-group">
        <label className="profile-sheet-label" htmlFor="new-period-start">
          Start date *
        </label>
        <input
          id="new-period-start"
          type="date"
          className="profile-sheet-input"
          value={newPeriod.start_date}
          onChange={(e) => {
            setNewPeriod((p) => ({ ...p, start_date: e.target.value }));
            setPeriodFormError('');
          }}
          required
          disabled={disabled}
        />
      </div>
      {newPeriod.is_permanent ? (
        <p className="profile-sheet-hint groups-create-period-permanent-end-note">
          Permanent periods do not have an end date.
        </p>
      ) : (
        <div className="profile-sheet-form-group">
          <label className="profile-sheet-label" htmlFor="new-period-end">
            End date *
          </label>
          <input
            id="new-period-end"
            type="date"
            className="profile-sheet-input"
            value={newPeriod.end_date}
            onChange={(e) => {
              setNewPeriod((p) => ({ ...p, end_date: e.target.value }));
              setPeriodFormError('');
            }}
            required
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );

  const variationForkPanel = (
    <>
            <p className="groups-create-period-combo-intro">
              Will this group need different periods over time? For example, students who enroll
              each year in a new grade with new data — choose <strong>Yes</strong>. For lasting
              teams (Sports, Admin, Owner) that stay the same, choose <strong>No</strong>.
            </p>
            <div className="groups-period-variation" role="radiogroup" aria-label="Period variation">
              <button
                type="button"
                role="radio"
                aria-checked={periodVariation === 'lasting'}
                className={`groups-period-variation-card${
                  periodVariation === 'lasting' ? ' is-selected' : ''
                }`}
                onClick={() => {
                  setPeriodVariation('lasting');
                  setShowCreatePeriod(false);
                  setPeriodFormError('');
                }}
                disabled={disabled}
              >
                <span className="groups-period-variation-card-title">No — lasting</span>
                <span className="groups-period-variation-card-note">
                  Creates this group’s own Permanent period. You can switch to an org/parent
                  Permanent if needed.
                </span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={periodVariation === 'varying'}
                className={`groups-period-variation-card${
                  periodVariation === 'varying' ? ' is-selected' : ''
                }`}
                onClick={() => {
                  setPeriodVariation('varying');
                  setFormData((prev) => ({
                    ...prev,
                    periodId: '',
                    groupPeriodTitle: '',
                    groupPeriodDescription: ''
                  }));
                  setShowCreatePeriod(false);
                  setNewPeriod({ ...EMPTY_NEW_PERIOD, is_permanent: false });
                  setPeriodFormError('');
                }}
                disabled={disabled}
              >
                <span className="groups-period-variation-card-title">Yes — changes by term</span>
                <span className="groups-period-variation-card-note">
                  Pick or create the first term period, then name this group–period combo.
                </span>
              </button>
            </div>

            {periodVariation === 'lasting' ? (
              <div className="groups-period-lasting-panel">
                {allowPermanentPeriodCreate ? (
                  <div
                    className={`groups-period-lasting-summary${
                      lastingWillCreatePermanent ? ' is-default' : ''
                    }`}
                  >
                    <strong>{ownPermanentTitle}</strong>
                    <span>
                      {lastingWillCreatePermanent
                        ? 'Default — this group’s own Permanent will be created when you click Create.'
                        : 'Using an existing Permanent below instead of creating a new one.'}
                    </span>
                    {!lastingWillCreatePermanent ? (
                      <button
                        type="button"
                        className="profile-section-add-btn"
                        onClick={() => {
                          setFormData((prev) => {
                            const comboName = prev.name || subjectName || 'Group';
                            const phantom = {
                              title: `Permanent -  ${comboName}`,
                              code: 'PERMANENT',
                              is_permanent: true
                            };
                            return {
                              ...prev,
                              periodId: '',
                              ...buildGroupPeriodComboSuggests(
                                comboName,
                                phantom,
                                organizationName,
                                periodDescriptionContext
                              )
                            };
                          });
                          setPeriodFormError('');
                        }}
                        disabled={disabled}
                      >
                        Use this group’s own Permanent
                      </button>
                    ) : null}
                  </div>
                ) : null}

                {allowPermanentPeriodCreate && permanentPeriods.length > 0 ? (
                  <>
                    <label
                      className="profile-sheet-label"
                      htmlFor="create-entity-period"
                      id="create-entity-period-label"
                    >
                      Or use an existing Permanent (org / parent)
                    </label>
                    <GroupsPeriodBadgePicker
                      id="create-entity-period"
                      periods={permanentPeriods}
                      value={formData.periodId}
                      required={false}
                      disabled={disabled}
                      placeholder="Select existing Permanent…"
                      onChange={(period) => applyPeriodToForm(period)}
                    />
                  </>
                ) : null}

                {!allowPermanentPeriodCreate && permanentPeriods.length === 0 ? (
                  <p className="profile-sheet-hint">
                    This group already has periods, so Permanent is not available. Choose “Yes —
                    changes by term,” or nest a new child group for Permanent.
                  </p>
                ) : null}
                {!allowPermanentPeriodCreate && permanentPeriods.length > 0 ? (
                  <>
                    <label
                      className="profile-sheet-label"
                      htmlFor="create-entity-period"
                      id="create-entity-period-label"
                    >
                      Permanent period *
                    </label>
                    <GroupsPeriodBadgePicker
                      id="create-entity-period"
                      periods={permanentPeriods}
                      value={formData.periodId}
                      required
                      disabled={disabled}
                      placeholder="Select a Permanent period…"
                      onChange={(period) => applyPeriodToForm(period)}
                    />
                  </>
                ) : null}

                {(formData.groupPeriodTitle || lastingWillCreatePermanent) &&
                (allowPermanentPeriodCreate || formData.periodId) ? (
                  <div className="groups-period-lasting-combo" aria-live="polite">
                    <p className="profile-sheet-label">Group–period (auto)</p>
                    <p className="profile-sheet-muted">
                      Title: {formData.groupPeriodTitle || '—'}
                    </p>
                    {formData.groupPeriodDescription ? (
                      <p className="profile-sheet-muted">
                        Description: {formData.groupPeriodDescription}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}

            {periodVariation === 'varying' ? (
              <div className="groups-period-varying-panel">
                <p className="groups-create-period-combo-intro">
                  For the <strong>first</strong> period of this group, pick an existing term or
                  create a new one. Then name and describe this group–period combo.
                </p>
                <div className="groups-create-period-label-row">
                  <label
                    className="profile-sheet-label"
                    htmlFor="create-entity-period"
                    id="create-entity-period-label"
                  >
                    First term period *
                  </label>
                  {organizationGroupId && allowNewPeriodCreate ? (
                    <button
                      type="button"
                      className="profile-section-add-btn"
                      onClick={() => {
                        setShowCreatePeriod((wasOpen) => {
                          const next = !wasOpen;
                          if (next) {
                            setFormData((prev) => ({ ...prev, periodId: '' }));
                            setNewPeriod({ ...EMPTY_NEW_PERIOD, is_permanent: false });
                          }
                          return next;
                        });
                        setPeriodFormError('');
                      }}
                      disabled={disabled}
                    >
                      {showCreatePeriod ? 'Use existing period' : '+ New period'}
                    </button>
                  ) : null}
                </div>
                {showCreatePeriod ? (
                  newPeriodPanel
                ) : termPeriods.length === 0 ? (
                  <p className="profile-sheet-hint">
                    No term periods yet. Use + New period to add the first one.
                  </p>
                ) : (
                  <GroupsPeriodBadgePicker
                    id="create-entity-period"
                    periods={termPeriods}
                    value={formData.periodId}
                    required
                    disabled={disabled}
                    onChange={(period) => applyPeriodToForm(period)}
                  />
                )}
              </div>
            ) : null}
    </>
  );

  return (
    <>
      {!hideGroupIdentity ? (
        <>
          <div className="profile-sheet-form-group">
            <label className="profile-sheet-label" htmlFor="create-entity-name">
              {nameFieldLabel} *
            </label>
            <input
              id="create-entity-name"
              type="text"
              className="profile-sheet-input"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              required
              disabled={disabled}
            />
          </div>
          <div className="profile-sheet-form-group">
            <label className="profile-sheet-label" htmlFor="create-entity-description">
              {copy.descriptionLabel}
            </label>
            <textarea
              id="create-entity-description"
              className="profile-sheet-textarea"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder={descriptionPlaceholder}
              rows={2}
              disabled={disabled}
            />
          </div>
        </>
      ) : null}
      <div className="profile-sheet-form-group groups-create-period-combo">
        {isTopLevelGroupCreate ? (
          variationForkPanel
        ) : (
          <>
            <div className="groups-create-period-label-row">
              <label
                className="profile-sheet-label"
                htmlFor="create-entity-period"
                id="create-entity-period-label"
              >
                {nestedTeamCreate ? "New group's time period *" : 'Time period *'}
              </label>
              {!lockPeriodToDefault &&
              organizationGroupId &&
              !nestedTeamCreate &&
              allowNewPeriodCreate ? (
                <button
                  type="button"
                  className="profile-section-add-btn"
                  onClick={() => {
                    setShowCreatePeriod((wasOpen) => {
                      const next = !wasOpen;
                      if (next) {
                        setFormData((prev) => ({ ...prev, periodId: '' }));
                      } else if (suggestedExistingPeriodId) {
                        const periodId = String(suggestedExistingPeriodId);
                        const period = selectablePeriods.find((p) => String(p.id) === periodId);
                        if (period) applyPeriodToForm(period);
                      }
                      return next;
                    });
                    setPeriodFormError('');
                  }}
                  disabled={disabled}
                >
                  {showCreatePeriod ? 'Use existing period' : '+ New period'}
                </button>
              ) : null}
            </div>
            {lockPeriodToDefault ? (
              <>
                <div className="profile-sheet-row">
                  <strong>Permanent</strong>
                  <span>Created with organization</span>
                </div>
                <p className="profile-sheet-hint">{copy.periodHint}</p>
              </>
            ) : nestedTeamCreate ? (
              <GroupsNestedTeamPeriodChoice
                parentPeriod={nestedParentPeriod}
                parentTeamName={nestedParentTeamName}
                choice={nestedPeriodChoice}
                onChoiceChange={onNestedPeriodChoiceChange}
                formData={formData}
                setFormData={setFormData}
                organizationName={organizationName}
                periodDescriptionContext={periodDescriptionContext}
                subjectName={subjectName}
                onShowCreatePeriodChange={setShowCreatePeriod}
                disabled={disabled}
              >
                {usesNestedOwnVariation ? variationForkPanel : null}
              </GroupsNestedTeamPeriodChoice>
            ) : showCreatePeriod ? (
              newPeriodPanel
            ) : (
              <>
                {selectablePeriods.length === 0 ? (
                  <p className="profile-sheet-hint">
                    {!allowNewPeriodCreate
                      ? 'This group is on Permanent — it cannot have additional term periods. Nest a child team for term calendars.'
                      : !allowPermanentPeriodCreate
                        ? 'No additional periods available for this group yet. Use + New period to add a term.'
                        : 'No time periods yet. Use + New period to add one (e.g. 2025–2026).'}
                  </p>
                ) : (
                  <GroupsPeriodBadgePicker
                    id="create-entity-period"
                    periods={periodsForPicker}
                    value={formData.periodId}
                    required={periodsForPicker.length > 0}
                    disabled={disabled}
                    onChange={(period) => applyPeriodToForm(period)}
                  />
                )}
              </>
            )}
          </>
        )}
        {periodFormError ? <div className="profile-sheet-error">{periodFormError}</div> : null}
      </div>

      {showGroupPeriodFields && !hideGroupPeriodTextFields && periodVariation !== 'lasting' ? (
        <>
          <div className="profile-sheet-form-group">
            <label className="profile-sheet-label" htmlFor="create-entity-gp-title">
              {usesVariationFork && periodVariation === 'varying'
                ? 'Name this first group–period combo *'
                : 'Group–period title *'}
            </label>
            <p className="profile-sheet-hint">
              How this group appears for {comboPeriodLabel} (suggested from the name + period).
            </p>
            <input
              id="create-entity-gp-title"
              type="text"
              className="profile-sheet-input"
              value={formData.groupPeriodTitle || ''}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, groupPeriodTitle: e.target.value }))
              }
              placeholder={gpTitleLabel}
              required
              disabled={disabled}
            />
          </div>
          <div className="profile-sheet-form-group">
            <label className="profile-sheet-label" htmlFor="create-entity-gp-desc">
              {usesVariationFork && periodVariation === 'varying'
                ? 'Describe this first group–period combo'
                : 'Group–period description'}
            </label>
            <textarea
              id="create-entity-gp-desc"
              className="profile-sheet-textarea"
              value={formData.groupPeriodDescription || ''}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, groupPeriodDescription: e.target.value }))
              }
              placeholder={gpDescLabel}
              rows={2}
              disabled={disabled}
            />
          </div>
        </>
      ) : null}
    </>
  );
});

export default GroupsCreateEntityFormFields;
